import { Fragment, useEffect, useRef, type ReactNode } from "react";
import { dayKey, dayLabel } from "./threadDays";

/**
 * A conversation in bubbles, the thread Petrovic's Konversation and the client's Chatt share.
 *
 * Decided 2026-09-24: the admin's client page keeps the bubbles of the sketch's version 1, and the
 * client's Chatt is version 1 too, so both sides of the same thread look like the same thread. The
 * second build round moves the client's screen onto this component; the first builds it for admin.
 *
 * Each side of the screen is one party. The one reading sits on the right, the other on the left
 * with a face. The side is also said in words to a screen reader, and never inferred from a name,
 * because a name can be missing and a side cannot.
 *
 * Days are marked between the bubbles in the reader's own words ("I dag", "I går", then the weekday
 * and the date), and a bubble carries only its time.
 */
export interface ThreadMessage {
  id: string;
  /** The reader's own side, drawn on the right. */
  mine: boolean;
  /** Said to a screen reader before the name: "Petrovic", "Kunden", "Du". */
  sideWord: string;
  author: string;
  initials: string;
  /** Petrovic's face on the client's screen carries the brand fill. */
  petrovic?: boolean;
  sentAt: string;
  body: string | null;
  /** What stands in the bubble when the message has no text of its own. Empty draws nothing. */
  missingBody?: string;
  attachments?: ReactNode;
  menu?: ReactNode;
  /** An inline form under this message, such as Skapa ärende. */
  after?: ReactNode;
  testId?: string;
  side?: string;
  /**
   * Where a message that is not the reader's own sits, added 2026-09-24 for the client's Chatt.
   *
   * The side says which of the two parties wrote it. A colleague of the reader belongs to the
   * reader's party, so `colleague` sits on the reader's side without the reader's tint and with a
   * face; `unknown` (a sender whose account is gone) belongs to neither and sits in the middle.
   * Petrovic's own screen has no such cases and never passes this.
   */
  placement?: "colleague" | "unknown";
  /** The reader's own name is kept for a screen reader and not drawn, as a phone's messaging app does. */
  authorHidden?: boolean;
}

/** The nearest ancestor that scrolls: the client shell's `.portal-main`, or the document itself. */
function scrollingAncestor(element: HTMLElement): Element {
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const overflow = window.getComputedStyle(parent).overflowY;
    if ((overflow === "auto" || overflow === "scroll") && parent.scrollHeight > parent.clientHeight + 1) {
      return parent;
    }
  }
  return document.scrollingElement ?? document.documentElement;
}

const timeOnly = new Intl.DateTimeFormat("sv-SE", { hour: "2-digit", minute: "2-digit" });
const fullDate = new Intl.DateTimeFormat("sv-SE", { day: "numeric", month: "long", year: "numeric" });

export function ThreadLog({
  messages,
  label,
  empty,
  scrollToEnd = true,
  live = false
}: {
  messages: ThreadMessage[];
  label: string;
  /** Rendered in place of the list when there is nothing in it. */
  empty?: ReactNode;
  scrollToEnd?: boolean;
  /**
   * A live region, so a message that arrives while the page is open is read out. `additions` only:
   * the log is only ever appended to, and announcing removals would read the whole history on a
   * refetch. The client's Chatt has had this since 2026-09-08 and keeps it.
   */
  live?: boolean;
}) {
  const log = useRef<HTMLOListElement>(null);
  const last = messages.length === 0 ? null : messages[messages.length - 1].id;
  const lastIsMine = messages.length === 0 ? false : messages[messages.length - 1].mine;
  const hasMessages = messages.length > 0;
  const opened = useRef(false);
  /*
   * Whether the reader is at the end, which only the reader's own scrolling changes.
   *
   * Build round 5 (2026-09-27), from Mattej's iPhone: the chat opened high up in the history and he
   * had to scroll down to the newest message. Scrolling to the end once, on the frame after the
   * messages arrive, is right only if nothing below grows after it. On a phone several things do: the
   * attachment chips come from a query of their own and land under their messages later, the
   * typeface swaps in and rewraps every bubble, and Safari settles the window's height after the
   * first paint. Each of those leaves the end below the fold while the scroll position stands still.
   * So a log that was at its end stays at its end when its content or its box changes size, until the
   * reader scrolls up themself.
   */
  const atEnd = useRef(true);

  /*
   * The newest message is at the bottom and is what a person opens a thread to read.
   *
   * On a computer the log is its own scroller and is taken to its end. On a phone it is not: the
   * log has no height limit there, the page is what scrolls, and the composer rides above the bottom
   * bar. Until 2026-09-25 only the log was scrolled, which on a phone did nothing, so Konversation
   * opened at the oldest message, a screen or ten above the answer box (measured on staging at 375
   * px after the first build round). Now the element that actually scrolls is taken to its end,
   * which on a thread page puts the newest message directly above the composer, the way a phone's
   * messaging app opens.
   *
   * A frame later rather than at once, because `RouteChrome` returns the page's scroller to the top
   * on every navigation and its effect runs after this one (effects run child first). A thread read
   * from the cache mounts in the same commit as the navigation, and scrolling before the reset would
   * be undone by it.
   */
  useEffect(() => {
    const element = log.current;
    if (!scrollToEnd || !element) {
      return;
    }
    /*
     * On opening, always. After that only for somebody who was already reading the newest message:
     * a message that arrives while a reader has scrolled up into last week must not throw them to
     * the bottom mid-sentence (the client's Chatt, 2026-09-08). The distance is measured after the
     * new message is in the document, so the threshold is larger than one message.
     */
    const first = !opened.current;
    const frame = window.requestAnimationFrame(() => {
      const scroller: Element =
        element.scrollHeight > element.clientHeight + 1 ? element : scrollingAncestor(element);
      /*
       * Two changes on 2026-09-27, build round 4, when the client's chat began to scroll inside its
       * box. The distance is measured without the new message, because it is where the reader was
       * before it arrived that decides whether they were reading the end: a five-line message on a
       * phone is taller than the threshold on its own, and was leaving the reader where they were.
       * And the reader's own message is always followed: somebody who pressed Skicka is looking at
       * the end by definition.
       */
      const newest = element.lastElementChild?.getBoundingClientRect().height ?? 0;
      const distance = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight - newest;
      if (first || lastIsMine || atEnd.current || distance <= 160) {
        scroller.scrollTo({ top: scroller.scrollHeight });
        atEnd.current = true;
      }
      opened.current = true;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [last, lastIsMine, scrollToEnd]);

  /*
   * Staying at the end. The scroll listener is on the document in the capture phase, because a
   * scroll event does not bubble and the element that scrolls is the log on the chat page and an
   * ancestor elsewhere. A scroll this component makes itself lands at the end and keeps the flag
   * true; only the reader moving up clears it. 48 px rather than the 160 above: this decides whether
   * to move somebody without a new message to show them, so it has to be sure they were at the end.
   */
  useEffect(() => {
    const element = log.current;
    if (!scrollToEnd || !element) {
      return;
    }

    const scroller = (): Element =>
      element.scrollHeight > element.clientHeight + 1 ? element : scrollingAncestor(element);

    /*
     * Only a move up leaves the end (build round 27, 2026-10-09). WebKit fires a scroll event on the
     * log when its content grows under a scroll position that stands still: measured on the iPhone 13
     * profile, the event came with scrollTop unchanged at 24 and the end 151 px below, before the
     * ResizeObserver's callback. Read as "the reader left the end", it cleared the flag, so the
     * observer never followed and the log stayed above the newest message (20 failures in 20). A
     * scroll position that did not go up is not the reader leaving.
     */
    let lastTop = scroller().scrollTop;
    const onScroll = (event: Event) => {
      const current = scroller();
      const target = event.target === document ? document.scrollingElement : event.target;
      if (target !== current) {
        return;
      }
      const top = current.scrollTop;
      const movedUp = top < lastTop - 1;
      lastTop = top;
      if (current.scrollHeight - top - current.clientHeight <= 48) {
        atEnd.current = true;
      } else if (movedUp) {
        atEnd.current = false;
      }
    };

    document.addEventListener("scroll", onScroll, { capture: true, passive: true });

    if (typeof ResizeObserver === "undefined") {
      return () => document.removeEventListener("scroll", onScroll, { capture: true });
    }

    let frame = 0;
    const follow = new ResizeObserver(() => {
      if (!atEnd.current) {
        return;
      }
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        /*
         * Asked again when the frame runs, not only when the size changed (build round 6,
         * 2026-09-27). A reader who scrolled up between the two had their scroll event before this
         * frame, since a browser runs scroll steps before animation frame callbacks, and was pulled
         * back down anyway: the full browser suite caught it once in 700 under load.
         */
        if (!atEnd.current) {
          return;
        }
        const current = scroller();
        current.scrollTo({ top: current.scrollHeight });
      });
    });

    // The box itself, for a window that changes height, and every row in it, for content that grows.
    // A row added later is observed from the moment it is added.
    follow.observe(element);
    for (const row of Array.from(element.children)) {
      follow.observe(row);
    }
    const added = new MutationObserver((records) => {
      for (const record of records) {
        record.addedNodes.forEach((node) => {
          if (node instanceof Element) {
            follow.observe(node);
          }
        });
      }
    });
    added.observe(element, { childList: true });

    return () => {
      window.cancelAnimationFrame(frame);
      follow.disconnect();
      added.disconnect();
      document.removeEventListener("scroll", onScroll, { capture: true });
    };
  }, [scrollToEnd, hasMessages]);

  if (messages.length === 0 && empty) {
    return <>{empty}</>;
  }

  // Which messages open a new day, decided before rendering rather than while it happens.
  const dayKeys = messages.map((message) => {
    const sent = new Date(message.sentAt);
    return Number.isNaN(sent.getTime()) ? "unknown" : dayKey(sent);
  });

  return (
    /*
      `tabIndex={0}` since build round 4 (2026-09-27): the client's chat scrolls inside this list now,
      and a region that scrolls and cannot be focused cannot be scrolled from the keyboard, which is
      WCAG 2.1.1 and what axe reported as `scrollable-region-focusable` on /chat. The same reason
      `.portal-main` carries it in `AppShell`. The label names it for the person who lands on it.
    */
    <ol
      className="thread-log"
      tabIndex={0}
      aria-label={label}
      aria-live={live ? "polite" : undefined}
      aria-relevant={live ? "additions" : undefined}
      ref={log}
    >
      {messages.map((message, index) => {
        const sent = new Date(message.sentAt);
        const valid = !Number.isNaN(sent.getTime());
        const newDay = index === 0 || dayKeys[index] !== dayKeys[index - 1];

        return (
          <Fragment key={message.id}>
            {newDay && valid ? (
              <li className="thread-day" aria-hidden="true">
                <span>{dayLabel(sent)}</span>
              </li>
            ) : null}
            <li
              className={`bubble-row${message.mine ? " is-mine" : ""}${message.placement ? ` is-${message.placement}` : ""}`}
              data-testid={message.testId}
              data-side={message.side}
            >
              {message.mine ? null : (
                <span className={`bubble-avatar${message.petrovic ? " is-petrovic" : ""}`} aria-hidden="true">
                  {message.initials}
                </span>
              )}
              <div className="bubble">
                <p className="bubble-meta">
                  <span className="visually-hidden" data-testid="chat-side">
                    {message.sideWord}
                  </span>
                  <strong className={message.authorHidden ? "visually-hidden" : undefined}>{message.author}</strong>
                  {valid ? (
                    <time dateTime={message.sentAt} title={fullDate.format(sent)}>
                      {timeOnly.format(sent)}
                    </time>
                  ) : null}
                  {message.menu}
                </p>
                {message.body === null && message.missingBody === "" ? null : message.body === null ? (
                  <p className="bubble-body is-missing" data-testid="chat-body-missing">
                    {message.missingBody ?? "Meddelandet har ingen text."}
                  </p>
                ) : (
                  <p className="bubble-body">{message.body}</p>
                )}
                {message.attachments}
              </div>
            </li>
            {message.after ? <li className="bubble-form">{message.after}</li> : null}
          </Fragment>
        );
      })}
    </ol>
  );
}
